import { useCallback, useEffect, useMemo, useState } from 'react'
import * as catalogos from '../../api/catalogos'
import * as clientesApi from '../../api/clientes'
import { useAuth } from '../../auth/AuthContext'
import { imponibleLinea, imponiblesCabecera } from '../../utils/iva'

// Línea de detalle vacía
const emptyProducto = () => ({ producto: null, unidadMedida: null, cantidad: 1, descuento: 0, precioVenta: 0, totalItem: 0, montosImponibles: null, cantidadUmPrincipal: 1 })
const emptyServicio = () => ({ servicio: null, cantidad: 1, descuento: 0, precioVenta: 0, totalItem: 0, montosImponibles: null })

// tipoImpuesto de un producto/servicio (producto.tiposImpuesto[0]; servicio idem o tipoImpuesto).
function tipoImpuestoDe(item) {
  return item?.tiposImpuesto?.[0] || item?.tipoImpuesto || null
}

// Setea montosImponibles[0] de la línea desde el ítem (port de changeProducto, form.js:5206-5218).
// Cliente exonerado de IVA => tasa 0.
function aplicarImpuesto(next, item, cliente) {
  const ti = cliente?.exoneradoIva
    ? { porcentaje: 0, descripcion: 'Exenta', exento: true }
    : tipoImpuestoDe(item)
  next.montosImponibles = [{ tipoImpuesto: ti, porcentaje: ti?.porcentaje ?? null, baseImponible: 0, valor: 0, valorExento: 0 }]
  return next
}

// Recalcula totalItem, IVA de la línea y cantidadUmPrincipal (port de calcularTotalDetalle).
function recalcLinea(next) {
  const cant = Number(next.cantidad) || 0
  const precio = Number(next.precioVenta) || 0
  const desc = Number(next.descuento) || 0
  const totalNeto = desc > 0 ? precio - precio * (desc / 100) : precio
  next.totalItem = Math.round(cant * totalNeto * 100) / 100

  const mi = next.montosImponibles?.[0]
  const pct = mi?.tipoImpuesto?.porcentaje ?? mi?.porcentaje
  if (mi && pct != null) {
    const { baseImponible, valor, valorExento } = imponibleLinea(next.totalItem, pct, next.fraccionExenta || 0)
    next.montosImponibles = [{ ...mi, baseImponible, valor, valorExento }]
  }

  // Cantidad U.M. Principal = cantidad * unidadMedida.cantidad (form.js:4381-4392)
  const contenida = next.unidadMedida?.cantidad ?? null
  next.cantidadUmPrincipal = cant > 0 && contenida != null
    ? parseFloat((cant * contenida).toFixed(4))
    : cant
  return next
}

const initialComprobante = () => ({
  cliente: null,
  sucursalCliente: null,
  vendedor: null,
  fecha: new Date().toISOString().slice(0, 10),
  deposito: null,
  tipo: null,
  moneda: null,
  cambio: 1,
  listaPrecioVenta: null,
  formaPago: null,
  imputarIvaGeneral: false,
  imputarIre: false,
  imputarIrpRsp: false,
})

// Trim a {id} (equivalente a ModelTrimmer.trimDetails, form.js:7223): objeto anidado -> {id}.
const idOf = (o) => (o && o.id != null ? { id: o.id } : null)

// montosImponibles con tipoImpuesto trimeado a {id}. Shape validado contra factura real id 2776:
// { valor, valorExento, total, baseImponible, porcentaje, tipoImpuesto:{id} }.
const trimMonto = (m) => ({
  tipoImpuesto: idOf(m.tipoImpuesto),
  porcentaje: m.porcentaje ?? m.tipoImpuesto?.porcentaje ?? 0,
  baseImponible: m.baseImponible ?? 0,
  valor: m.valor ?? 0,
  valorExento: m.valorExento ?? 0,
  total: m.total ?? null,
})

// Hook con todo el estado del formulario de Facturación Ágil (port de activateNewAgil + cálculos).
export function useFacturacionAgil() {
  const { auth } = useAuth()
  const [loading, setLoading] = useState(true)
  const [initError, setInitError] = useState(null)

  const [timbradoAgil, setTimbradoAgil] = useState(null)
  const [comprobante, setComprobante] = useState(initialComprobante)
  const [productos, setProductos] = useState([emptyProducto()])
  const [servicios, setServicios] = useState([emptyServicio()])

  // catálogos
  const [monedas, setMonedas] = useState([])
  const [tipos, setTipos] = useState([])
  const [listasPreciosVentas, setListasPreciosVentas] = useState([])
  const [formasPagos, setFormasPagos] = useState([])
  const [sucursalesCliente, setSucursalesCliente] = useState([])
  const [cotizaciones, setCotizaciones] = useState([])
  const [tipoTransaccion, setTipoTransaccion] = useState(null) // default 'venta_mercaderia'
  const [almacenes, setAlmacenes] = useState([])
  const [cobroParams, setCobroParams] = useState({ estadoAbierto: null, tipoCobro: null, tipoCobroEntidad: null })

  const setField = useCallback((name, value) => {
    setComprobante((c) => ({ ...c, [name]: value }))
  }, [])

  // ---- carga inicial ----
  // Orden / contratos según activateNewAgil (form.js:2146+) y VERIFICADOS EN VIVO (2026-06-25):
  //  - Cliente default ágil: GET /clientes?view=BaseList&search=and(activo,factAgilDefault) -> item rico con sucursales.
  //  - Tipos comprobante ágil: GET /tipocomprobantes?view=ConsultaComprobanteVentaForm&search=and(...) -> lista; default = contado.
  //  - Timbrado ágil: filtrado por puntoEmisionVenta.id == caja.puntoEmision.id (caja del usuario, de loginApp).
  //  - Monedas / listas / formas de pago / cotizaciones: GET directos.
  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      setInitError(null)

      // La caja del usuario (de loginApp -> accesoSistema.cajas) define el punto de emisión del timbrado.
      const caja = auth?.caja || null
      const puntoEmisionId = caja?.puntoEmision?.id ?? null

      try {
        const [clienteDefP, tiposP, monP, listasP, formasP, cotP, ttP, almP, tcobP, estProcP, tceP] = await Promise.allSettled([
          clientesApi.getClienteDefaultAgil(),
          catalogos.getTiposComprobanteAgil(),
          catalogos.getMonedas(),
          catalogos.getListasPrecioVenta(),
          catalogos.getFormasPago(),
          catalogos.getCotizaciones(),
          catalogos.getTiposTransaccion(),
          catalogos.getAlmacenes(),
          catalogos.getTiposCobro(),
          catalogos.getEstadosProcesoCobro(),
          catalogos.getTiposCobroEntidad(),
        ])
        if (!alive) return

        const ok = (r) => (r.status === 'fulfilled' ? r.value : null)
        const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : [])

        const clienteDefault = ok(clienteDefP) // ya es un objeto (o null), no array
        const tiposList = asArray(ok(tiposP))
        const monList = asArray(ok(monP))
        const cotList = asArray(ok(cotP))

        setTipos(tiposList)
        setMonedas(monList)
        setListasPreciosVentas(asArray(ok(listasP)))
        setFormasPagos(asArray(ok(formasP)))
        setCotizaciones(cotList)
        setSucursalesCliente(clienteDefault?.sucursales || [])
        const ttList = asArray(ok(ttP))
        setTipoTransaccion(ttList.find((t) => t?.codigo === 'venta_mercaderia') || null)
        const almList = asArray(ok(almP))
        setAlmacenes(almList)
        setCobroParams({
          estadoAbierto: asArray(ok(estProcP)).find((t) => t?.codigo === 'estado_procesocobro_abierto') || null,
          tipoCobro: asArray(ok(tcobP)).find((t) => t?.codigo === 'tipo_cobro_normal') || null,
          tipoCobroEntidad: asArray(ok(tceP)).find((t) => /contado/i.test(t?.nombre || t?.descripcion || '')) || null,
        })

        const tipoDefault = catalogos.pickTipoDefault(tiposList)
        const monedaLocal =
          monList.find((m) => (m.codigo || '').toLowerCase() === 'guaranies') || monList[0] || null

        setComprobante((c) => ({
          ...c,
          tipo: tipoDefault,
          moneda: monedaLocal,
          cliente: clienteDefault || null,
          sucursalCliente: clienteDefault?.sucursales?.[0] || null,
          deposito: almList[0] || null,
        }))

        // Timbrado ágil: depende del tipo default (clase.codigo) y de la caja del usuario.
        // recuperarTimbradoFacturacionAgil (form.js:9072): filtra por puntoEmisionVenta.id +
        // tipoComprobante.codigo == tipo.clase.codigo. Si no hay caja => no se puede resolver.
        let timbrado = null
        if (puntoEmisionId != null) {
          const claseCodigo = tipoDefault?.clase?.codigo || 'factura'
          try {
            timbrado = await catalogos.getTimbradoAgil({ puntoEmisionId, claseComprobanteCodigo: claseCodigo })
          } catch (e) {
            console.warn('No se pudo recuperar el timbrado ágil', e)
          }
        }
        if (!alive) return
        setTimbradoAgil(timbrado)

        // Mensajes de bloqueo equivalentes a los del front Angular.
        if (tiposList.length === 0) {
          setInitError('No se encontraron Tipos de Comprobantes para la Facturación Ágil.')
        } else if (!caja) {
          setInitError('El usuario no tiene una caja registrada para su sucursal. Sin caja no se puede abrir el cobro.')
        } else if (!timbrado) {
          setInitError('No se encontró un Timbrado para la Facturación Ágil.')
        }
      } catch (e) {
        if (alive) setInitError('Error cargando datos iniciales de facturación.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
    // auth.caja se resuelve en login; recargamos si cambia la sesión.
  }, [auth?.caja])

  // ---- detalle: productos ----
  const addProducto = () => setProductos((p) => [...p, emptyProducto()])
  const removeProducto = (i) => setProductos((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p))
  const updateProducto = (i, patch) =>
    setProductos((p) => p.map((d, idx) => {
      if (idx !== i) return d
      let next = { ...d, ...patch }
      if (patch.producto !== undefined) next = aplicarImpuesto(next, patch.producto, comprobante.cliente)
      return recalcLinea(next)
    }))

  // Alta de una línea de producto ya con datos (desde el toolbar POS). Reusa la primera línea vacía.
  const addProductoConDatos = (producto, extra = {}) =>
    setProductos((p) => {
      const linea = recalcLinea(aplicarImpuesto(
        { ...emptyProducto(), producto, unidadMedida: producto?.unidadesMedida?.[0] || null, precioVenta: producto?.precioVenta || 0, ...extra },
        producto, comprobante.cliente,
      ))
      const idxVacia = p.findIndex((d) => !d.producto)
      return idxVacia >= 0 ? p.map((d, i) => (i === idxVacia ? linea : d)) : [...p, linea]
    })

  // ---- detalle: servicios ----
  const addServicio = () => setServicios((s) => [...s, emptyServicio()])
  const addServicioConDatos = (servicio, extra = {}) =>
    setServicios((s) => {
      const linea = recalcLinea(aplicarImpuesto(
        { ...emptyServicio(), servicio, precioVenta: servicio?.precioVenta || 0, ...extra },
        servicio, comprobante.cliente,
      ))
      const idxVacia = s.findIndex((d) => !d.servicio)
      return idxVacia >= 0 ? s.map((d, i) => (i === idxVacia ? linea : d)) : [...s, linea]
    })
  const removeServicio = (i) => setServicios((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s))
  const updateServicio = (i, patch) =>
    setServicios((s) => s.map((d, idx) => {
      if (idx !== i) return d
      let next = { ...d, ...patch }
      if (patch.servicio !== undefined) next = aplicarImpuesto(next, patch.servicio, comprobante.cliente)
      return recalcLinea(next)
    }))

  // cambia el cliente => recarga sucursales.
  // El typeahead ya devuelve el cliente con sucursales (view=BaseList). Por robustez, si el
  // item viniera "fino" (sin sucursales), traemos el detalle por id (GET /clientes/{id}?view=BaseList).
  const changeCliente = useCallback(async (cliente) => {
    if (!cliente) {
      setComprobante((c) => ({ ...c, cliente: null, sucursalCliente: null }))
      setSucursalesCliente([])
      return
    }
    let full = cliente
    if ((!cliente.sucursales || cliente.sucursales.length === 0) && cliente.id) {
      try {
        full = await clientesApi.getCliente(cliente.id)
      } catch (e) {
        console.warn('No se pudo traer el detalle del cliente', e)
      }
    }
    setComprobante((c) => ({ ...c, cliente: full, sucursalCliente: full?.sucursales?.[0] || null }))
    setSucursalesCliente(full?.sucursales || [])
  }, [])

  // ---- totales ----
  const totales = useMemo(() => {
    const productosConItem = productos.filter((d) => d.producto)
    const serviciosConItem = servicios.filter((d) => d.servicio)
    const totalProductos = productosConItem.reduce((acc, d) => acc + (Number(d.totalItem) || 0), 0)
    const totalServicios = serviciosConItem.reduce((acc, d) => acc + (Number(d.totalItem) || 0), 0)
    return { totalProductos, totalServicios, totalGeneral: totalProductos + totalServicios }
  }, [productos, servicios])

  // Impuestos de cabecera (IVA 10/5/exenta) para mostrar y validar (port de cargarImpuestos).
  const impuestos = useMemo(
    () => imponiblesCabecera([
      ...productos.filter((d) => d.producto),
      ...servicios.filter((d) => d.servicio),
    ]).montosImponibles,
    [productos, servicios],
  )

  const esContado = comprobante.tipo?.condicion?.codigo === 'tipo_comprobante_contado' || !comprobante.tipo

  // ---- validación (port de submit()) ----
  const validate = () => {
    const errs = []
    if (!comprobante.cliente) errs.push('Seleccioná un cliente.')
    if (!comprobante.sucursalCliente) errs.push('Seleccioná la sucursal del cliente.')
    if (!comprobante.fecha) errs.push('Ingresá la fecha.')
    if (!comprobante.tipo) errs.push('Seleccioná el tipo de comprobante.')
    if (!comprobante.moneda) errs.push('Seleccioná la moneda.')
    if (!timbradoAgil) errs.push('No hay timbrado ágil resuelto (no se puede emitir).')
    const hayProducto = productos.some((d) => d.producto)
    const hayServicio = servicios.some((d) => d.servicio)
    if (!hayProducto && !hayServicio) errs.push('Agregá al menos un producto o servicio (detalle vacío).')
    if (hayProducto && !comprobante.deposito) errs.push('Seleccioná el depósito (obligatorio para productos).')
    return errs
  }

  // ---- armado del payload de POST /comprobanteventa?facturacionagil=true ----
  // Shape VALIDADO con un POST REAL exitoso (comprobante 2817 emitido, estado comp_venta_emitido):
  //  - Objetos anidados trimeados a {id}; locacion/sucursal como id PELADO (form.js:7261-7263).
  //  - `deposito` es OBLIGATORIO: sin él, el backend NPE-ea en validarYAsignarListaPrecios
  //    (getDeposito().getModificarPrecioEspecial() sin null-check) cuando el cliente tiene lista.
  //  - `fecha` en formato 'yyyy/MM/dd' (con barras). Date-only con guiones rompe la deserialización.
  //  - El precio de línea es `precioVenta` y debe ser >= el mínimo de la lista del cliente
  //    (si no, el backend devuelve "ERROR DE VALIDACIÓN DE PRECIOS").
  //  - tipoTransaccion='venta_mercaderia'; tipoOperacion=cliente.tipoOperacion (ej. B2B).
  //  - montosImponibles por línea {valor, valorExento, total, baseImponible, porcentaje, tipoImpuesto:{id}}.
  //  - IVA calculado en el front (el backend NO recalcula: setTotales comentado :528/598).
  // Con facturacionagil=true el backend auto-aprueba (no se llama aprobarPorId).
  const buildPayload = () => {
    const loc = auth?.locacion || null
    const lineasProd = productos.filter((d) => d.producto)
    const lineasServ = servicios.filter((d) => d.servicio)
    const cab = imponiblesCabecera([...lineasProd, ...lineasServ])
    const comprobanteDetalle = [
      ...lineasProd.map((d) => ({
        producto: idOf(d.producto),
        unidadMedida: idOf(d.unidadMedida),
        cantidad: Number(d.cantidad),
        cantidadUmPrincipal: d.cantidadUmPrincipal ?? Number(d.cantidad),
        descuento: Number(d.descuento) || 0,
        montoDescuento: 0,
        proporcionGravadaIva: 100,
        precioVenta: Number(d.precioVenta) || 0,
        totalItem: d.totalItem,
        fraccionExenta: Number(d.fraccionExenta) || 0,
        montosImponibles: (d.montosImponibles || []).map(trimMonto),
      })),
      ...lineasServ.map((d) => ({
        servicio: idOf(d.servicio),
        cantidad: Number(d.cantidad),
        descuento: Number(d.descuento) || 0,
        montoDescuento: 0,
        precioVenta: Number(d.precioVenta) || 0,
        totalItem: d.totalItem,
        montosImponibles: (d.montosImponibles || []).map(trimMonto),
      })),
    ].map((it, i) => ({ ...it, orden: i + 1 }))
    return {
      clase: idOf(comprobante.tipo?.clase),
      tipo: idOf(comprobante.tipo),
      cliente: idOf(comprobante.cliente),
      sucursalCliente: idOf(comprobante.sucursalCliente),
      vendedor: idOf(comprobante.vendedor),
      moneda: idOf(comprobante.moneda),
      cambio: Number(comprobante.cambio) || 1,
      fecha: (comprobante.fecha || '').replace(/-/g, '/'), // Qualita espera 'yyyy/MM/dd'
      deposito: idOf(comprobante.deposito),
      timbradoPuntoEmision: idOf(timbradoAgil),
      listaPrecioVenta: idOf(comprobante.listaPrecioVenta),
      formaPago: idOf(comprobante.formaPago),
      tipoTransaccion: idOf(tipoTransaccion),
      tipoOperacion: idOf(comprobante.cliente?.tipoOperacion),
      imputarIvaGeneral: comprobante.imputarIvaGeneral,
      imputarIre: comprobante.imputarIre,
      imputarIrpRsp: comprobante.imputarIrpRsp,
      locacion: loc?.id ?? null,
      sucursal: loc?.sucursal?.id ?? null,
      comprobanteDetalle,
      montosImponibles: cab.montosImponibles.map(trimMonto),
      montoTotal: cab.montoTotal || totales.totalGeneral,
    }
  }

  // Helper: cotización del día para una moneda extranjera (monedaDesde.codigo) -> valorVenta.
  // VIVO: cotizacionmonedas = [{ monedaDesde:{codigo}, monedaHasta:{codigo:'guaranies'}, fecha, valorVenta }].
  const cotizacionDeMoneda = useCallback((moneda) => {
    if (!moneda || (moneda.codigo || '').toLowerCase() === 'guaranies') return 1
    const filas = cotizaciones
      .filter((c) => c?.monedaDesde?.codigo === moneda.codigo)
      .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)))
    return filas[0]?.valorVenta || filas[0]?.valorCompra || null
  }, [cotizaciones])

  const reset = () => {
    setComprobante((c) => ({ ...initialComprobante(), tipo: c.tipo, moneda: c.moneda, cliente: c.cliente, sucursalCliente: c.sucursalCliente }))
    setProductos([emptyProducto()])
    setServicios([emptyServicio()])
  }

  return {
    loading, initError,
    timbradoAgil,
    caja: auth?.caja || null,
    comprobante, setField, changeCliente,
    productos, addProducto, addProductoConDatos, removeProducto, updateProducto,
    servicios, addServicio, addServicioConDatos, removeServicio, updateServicio,
    monedas, tipos, listasPreciosVentas, formasPagos, sucursalesCliente, cotizaciones, almacenes, cobroParams,
    totales, impuestos, esContado, cotizacionDeMoneda,
    validate, buildPayload, reset,
  }
}
